import { motion } from 'motion/react';
import * as Icons from 'lucide-react';
import { useSiteConfig, resolveUploadUrl } from '../utils/siteConfig';

export function Services() {
  const { config, openGetStarted } = useSiteConfig();
  const services = config.services;

  return (
    <div className="pt-24 bg-slate-50 min-h-screen">
      <motion.section
        className="py-16 px-4 md:px-8 relative overflow-hidden"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.8 }}
      >
        {/* Decorative blobs */}
        <motion.div
          className="absolute top-20 right-0 w-72 h-72 bg-gradient-to-br from-primary/10 to-primary/10 rounded-full blur-3xl pointer-events-none"
          animate={{ x: [0, -40, 0], y: [0, 40, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section heading */}
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="px-3 py-1 bg-primary/5 border border-primary/20 text-primary rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-3 shadow-sm">
              Our Core Services
            </span>
            <h2 className="text-3xl md:text-5xl mb-4 text-slate-900 font-black tracking-tight heading-glow">What We Do Best</h2>
            <p className="text-base text-slate-600 max-w-xl mx-auto leading-relaxed font-sans">
              Comprehensive digital solutions tailored to build powerful brands, accelerate user acquisition, and drive long-term business growth.
            </p>
          </motion.div>

          {/* Service cards — 3 columns on desktop */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const IconComponent = (Icons as Record<string, any>)[service.icon] || Icons.Zap;
              return (
                <motion.div
                  key={index}
                  className="group"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: index * 0.08 }}
                >
                  <div className="rounded-[2.5rem] overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col">
                    {/* Image */}
                    <div className="h-52 overflow-hidden relative bg-slate-50 border-b border-slate-100">
                      {service.image ? (
                        <img
                          src={resolveUploadUrl(service.image)}
                          alt={service.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-14 h-14 bg-gradient-to-br from-primary/10 to-primary/10 border border-primary/15 rounded-xl flex items-center justify-center shadow-sm">
                            <IconComponent size={28} className="text-primary" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Card Content */}
                    <div className="p-8 flex flex-col flex-grow bg-white">
                      <div className="flex items-center gap-3.5 mb-4">
                        <div className="w-9 h-9 bg-primary/10 border border-primary/20 rounded-xl flex items-center justify-center text-primary shadow-sm shrink-0">
                          <IconComponent size={18} />
                        </div>
                        <h3 className="text-xl font-bold text-slate-900 leading-tight">{service.title}</h3>
                      </div>
                      
                      <p className="text-slate-500 text-sm leading-relaxed font-sans flex-grow">
                        {service.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Additional High-Value Services Section */}
      <section className="py-24 px-4 md:px-8 bg-white border-t border-slate-100 relative overflow-hidden">
        {/* Decorative background */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-primary/20" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/3 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10">
          {/* Section heading */}
          <div className="text-center mb-16">
            <span className="px-3 py-1 bg-primary/5 border border-primary/20 text-primary rounded-full text-xs font-semibold uppercase tracking-wider inline-block mb-3 shadow-sm">
              More Offerings
            </span>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-slate-900 mb-4 heading-glow">
              Additional High-Value Services
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto rounded-full mt-4" />
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-1 gap-6 max-w-3xl mx-auto w-full">
            {(config.additionalServices || []).map((svc, i) => {
              const IconComponent = (Icons as Record<string, any>)[svc.icon] || Icons.Zap;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  onClick={openGetStarted}
                  className="group bg-white border border-slate-100 rounded-[2rem] shadow-sm hover:shadow-xl hover:shadow-primary/5 hover:border-primary/30 hover:-translate-y-1.5 transition-all duration-300 p-8 cursor-pointer flex flex-col gap-4"
                >
                  {/* Icon */}
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `color-mix(in srgb, var(--primary) 10%, transparent)` }}
                  >
                    <IconComponent size={22} style={{ color: `var(--primary)` }} />
                  </div>

                  {/* Title in gradient brand colors */}
                  <h3
                    className="text-xl font-extrabold leading-snug bg-clip-text text-transparent"
                    style={{ backgroundImage: `linear-gradient(135deg, var(--primary), var(--accent))` }}
                  >
                    {svc.title}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-500 text-sm leading-relaxed font-sans flex-grow">
                    {svc.description}
                  </p>

                  {/* CTA */}
                  <div
                    className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest mt-auto opacity-60 group-hover:opacity-100 transition-opacity"
                    style={{ color: `var(--primary)` }}
                  >
                    Get Started <Icons.ArrowRight size={13} />
                  </div>
                </motion.div>
              );
            })}
            {(!config.additionalServices || config.additionalServices.length === 0) && (
              <div className="col-span-3 text-center py-16 text-slate-400">No additional services configured.</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
